/**
 * Messaging v2 — Server-Sent Events stream that replaces poll-driven
 * cache invalidation as the primary trigger.
 *
 * Lifecycle:
 *   idle → connecting → open → (event* | heartbeat*) → (network drop)
 *                                                    → closed → connecting (backoff)
 *                                                    → open … and so on.
 *
 * The hook NEVER throws. On environments that don't support fetch +
 * ReadableStream (very old Safari) it surfaces `status: 'error'` once and
 * gives up — polling alone keeps the inbox alive.
 *
 * Why fetch + ReadableStream and not EventSource?
 *   EventSource cannot set custom headers. Our SSE endpoint authenticates via
 *   `Authorization: Bearer <jwt>` like the rest of the messaging API.
 *
 * Why an inline parser and no new dependency?
 *   The SSE wire format we consume is a tight subset — `event:` + `data:` field
 *   pairs separated by `\n\n`. The parser is ~30 lines, so a dependency carries
 *   no benefit here.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth.store';
import { useLeasedStream } from '@/hooks/lib/useStreamLease';
import { useInboxActions, useInboxStore } from '@/stores/inbox.store';
import { getActiveTenantId } from '@/lib/activeTenant';
import { callLogKeys, messagingKeys } from '@/lib/queryKeys';
import { applyRealtimeMessage, type RealtimeMessagePayload } from '@/lib/messaging/realtimeMessage';
import { escalationLabel } from '@/lib/messaging/escalation';
import { useVoipStore } from '@/stores/voip.store';
import { recoverSession, redirectToLogin } from '@/lib/api/session';

// ─── Public types ─────────────────────────────────────────────────────────────

export type InboxStreamStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface UseInboxStreamOptions {
  /** Currently-open thread id (so we know which thread cache to refresh).
   *  Omit it entirely — as the dashboard layout does — to leave the value to
   *  whichever screen is actually managing it; see {@link useActiveInboxThread}. */
  activeThreadId?: string | null;
  /** Set to false to disable the stream (e.g. in tests). */
  enabled?: boolean;
}

export interface UseInboxStreamResult {
  status: InboxStreamStatus;
  lastEventAt: Date | null;
}

// ─── Wire-format types ────────────────────────────────────────────────────────

type InboxEventType =
  | 'message.received'
  | 'message.sent'
  | 'message.status_changed'
  // Iris "thinking" hint. Carries no message — it toggles the thread's typing
  // indicator, so it drives inbox-store state instead of invalidating caches.
  | 'ai.generating'
  | 'thread.reopened'
  | 'thread.reassigned'
  | 'thread.escalated'
  // An operator paused or resumed Iris on a thread. Resume clears an
  // escalation, so the list (and its Escalated count) plus the open thread's
  // header/banner must refresh in every inbox, not just the one that clicked.
  | 'thread.ai_toggled'
  | 'thread.section_changed'
  // Voice lifecycle (NOT-15). The voip store drives live call UI (screen-pop);
  // here we only refresh the call-history list when a call's record changes.
  | 'call.incoming'
  | 'call.answered'
  | 'call.answered_elsewhere'
  // The caller is now connected to their agent. The voip store waits on this:
  // the browser cannot observe the bridge for itself, and everything it CAN
  // see — its own ICE, silence from the caller — is the same before and after.
  | 'call.bridged'
  | 'call.missed'
  | 'call.ended'
  | 'call.completed'
  | 'call.voicemail'
  | 'heartbeat';

interface InboxEventBase {
  tenantId: string;
  threadId: string;
}

type MessageEventPayload = InboxEventBase &
  RealtimeMessagePayload & {
    messageId: string;
  };

/** `thread.escalated` — Iris handed the thread to a human; why, when. */
interface ThreadEscalatedPayload extends InboxEventBase {
  escalatedAt?: string;
  trigger?: string | null;
  detail?: string | null;
}

interface AiGeneratingPayload extends InboxEventBase {
  generating: boolean;
}

interface InboxStreamEvent {
  type: InboxEventType;
  data: Record<string, unknown> | null;
}

// ─── Tuning constants ─────────────────────────────────────────────────────────

const EVENTS_URL = '/api/messaging/events';
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 30_000;
/** Jitter ratio applied to each backoff delay (0 → no jitter, 1 → full). */
const JITTER_RATIO = 0.25;

// ─── Capability detection ────────────────────────────────────────────────────

function supportsStreamingFetch(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.fetch !== 'function') return false;
  if (typeof window.ReadableStream !== 'function') return false;
  if (typeof TextDecoder !== 'function') return false;
  return true;
}

// ─── Resolve operations base URL the same way the axios client does ──────────

function resolveOperationsBaseUrl(): string {
  const env = import.meta.env;
  const opsUrl = env.VITE_OPERATIONS_URL ?? '';
  // Strip trailing slashes — the events path begins with `/`.
  return typeof opsUrl === 'string' ? opsUrl.replace(/\/+$/, '') : '';
}

// ─── Cache invalidation per event type ───────────────────────────────────────

function invalidateForEvent(
  qc: QueryClient,
  evt: InboxStreamEvent,
  activeThreadIdRef: { current: string | null | undefined },
): void {
  if (evt.type === 'heartbeat') return;

  // Voice events aren't thread-scoped — they refresh the call-history list
  // (keyed by callControlId, not threadId). A no-op when the Calls tab isn't
  // mounted (no active callLogs query to refetch).
  if (evt.type.startsWith('call.')) {
    void qc.invalidateQueries({ queryKey: callLogKeys.all() });
    // The bar over a call reads "Connecting…" until the caller can actually
    // hear the agent. This is that moment, stated by the only party that knows
    // it; without it the store is reduced to guessing at a number, and a guess
    // that clears the bar early costs the agent's opening words.
    if (evt.type === 'call.bridged' && evt.data) {
      const d = evt.data as {
        callControlId?: string;
        metadata?: { agentLeg?: string | null } | null;
      };
      useVoipStore.getState().applyCallBridged({
        callControlId: d.callControlId,
        agentLeg: d.metadata?.agentLeg ?? null,
      });
      return;
    }
    // Screen-pop enrichment: `call.incoming` carries the matched CRM contact
    // (operations' caller-ID lookup). Feed it to the voip store, which
    // correlates it to the Telnyx ring by phone number and enriches the
    // incoming toast (Telnyx alone only gives us the raw number).
    if (evt.type === 'call.incoming' && evt.data) {
      const d = evt.data as {
        from?: string;
        contactId?: string;
        contactName?: string;
      };
      useVoipStore.getState().applyCallIncoming({
        from: d.from,
        contactId: d.contactId,
        contactName: d.contactName,
      });
    }
    return;
  }

  const payload = evt.data as Partial<InboxEventBase> | null;
  const threadId = payload?.threadId;

  // `generating: true` proves Iris has started processing a committed inbound
  // message. Recover from a dropped/reconnecting message.received frame by
  // fetching that thread once; the false frame carries no new data.
  if (evt.type === 'ai.generating') {
    const aiPayload = evt.data as Partial<AiGeneratingPayload> | null;
    if (!aiPayload?.generating || !threadId) return;
    void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    if (activeThreadIdRef.current === threadId) {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({
        queryKey: [...messagingKeys.thread(threadId), 'messages'],
      });
    }
    return;
  }

  // Every non-heartbeat event affects the threads list (last message preview,
  // unread badge, status). Cheap to refire — react-query dedupes in-flight.
  void qc.invalidateQueries({ queryKey: messagingKeys.threads() });

  if (!threadId) return;

  switch (evt.type) {
    case 'message.received':
    case 'message.sent':
    case 'message.status_changed': {
      // Only invalidate the open thread's caches — invalidating every cached
      // thread detail would force re-renders for threads the user isn't even
      // looking at. The list-level refresh above carries the unread bump.
      if (activeThreadIdRef.current === threadId) {
        void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
        void qc.invalidateQueries({
          queryKey: [...messagingKeys.thread(threadId), 'messages'],
        });
      }
      return;
    }
    case 'thread.reopened':
    case 'thread.reassigned':
    case 'thread.escalated':
    case 'thread.ai_toggled':
    case 'thread.section_changed': {
      void qc.invalidateQueries({ queryKey: messagingKeys.inboxes() });
      // Status / assignee changes can affect ANY visible thread row, but the
      // open-thread detail (`useThread`) is the only place we render the full
      // status pill / assignee chip — so it's the only `thread()` key we need
      // to refresh beyond the list query above.
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      return;
    }
    default:
      // Unknown event types are ignored — the backend may add new ones first.
      return;
  }
}

// ─── "Iris is thinking" indicator state ──────────────────────────────────────
//
// The `ai.generating` event toggles a per-thread typing indicator. We keep the
// set of generating thread ids in the inbox zustand store so the open thread
// (rendered by a sibling component) can read it without prop-drilling. As a
// belt-and-suspenders guard, an outbound message landing (`message.sent`) also
// clears the flag — so the dots never get stuck if a `generating: false` frame
// is ever dropped.

function applyGeneratingState(evt: InboxStreamEvent): void {
  const setThreadGenerating = useInboxStore.getState().setThreadGenerating;

  if (evt.type === 'ai.generating') {
    const payload = evt.data as Partial<AiGeneratingPayload> | null;
    if (payload?.threadId) {
      setThreadGenerating(payload.threadId, Boolean(payload.generating));
    }
    return;
  }

  // The bot's reply (or any outbound send) means it's no longer "thinking".
  if (evt.type === 'message.sent') {
    const payload = evt.data as Partial<InboxEventBase> | null;
    if (payload?.threadId) setThreadGenerating(payload.threadId, false);
  }
}

// ─── SSE wire-format parser ──────────────────────────────────────────────────
//
// Each event is a block of `field: value\n` lines terminated by a blank line
// (`\n\n`). We accumulate text in a buffer, split on `\n\n`, and parse each
// block. The parser is intentionally minimal — it ignores `id:` and `retry:`
// fields because our server doesn't send them.

function parseSseBlock(block: string): InboxStreamEvent | null {
  let type: string | null = null;
  const dataLines: string[] = [];

  for (const rawLine of block.split('\n')) {
    if (rawLine.startsWith(':')) continue; // SSE comment / keepalive line
    const colon = rawLine.indexOf(':');
    if (colon === -1) continue;
    const field = rawLine.slice(0, colon);
    // Per spec, a single leading space after the colon is stripped.
    const value = rawLine.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') type = value;
    else if (field === 'data') dataLines.push(value);
  }

  if (!type) return null;
  const dataStr = dataLines.join('\n');
  let data: Record<string, unknown> | null = null;
  if (dataStr.length > 0) {
    try {
      data = JSON.parse(dataStr) as Record<string, unknown>;
    } catch {
      // Malformed payload — surface as event-with-no-data so the consumer
      // can still treat it as a refresh trigger.
      data = null;
    }
  }
  return { type: type as InboxEventType, data };
}

async function consumeStream(
  response: Response,
  onEvent: (evt: InboxStreamEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const body = response.body;
  if (!body) throw new Error('SSE response has no body');

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });

      // Split on `\n\n` (event-delimiter). Tolerate `\r\n\r\n` too — some
      // proxies normalize line endings.
      let sepIdx: number;
      while ((sepIdx = findEventBoundary(buffer)) !== -1) {
        const block = buffer.slice(0, sepIdx).replace(/\r\n/g, '\n');
        // Move past the boundary (which is either 2 or 4 chars).
        const boundaryLen = buffer.startsWith('\r\n\r\n', sepIdx) ? 4 : 2;
        buffer = buffer.slice(sepIdx + boundaryLen);
        if (block.trim().length === 0) continue;
        const evt = parseSseBlock(block);
        if (evt) onEvent(evt);
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // Reader may already be released if the stream errored — ignore.
    }
  }
}

function findEventBoundary(buf: string): number {
  const lf = buf.indexOf('\n\n');
  const crlf = buf.indexOf('\r\n\r\n');
  if (lf === -1) return crlf;
  if (crlf === -1) return lf;
  return Math.min(lf, crlf);
}

// ─── Backoff ──────────────────────────────────────────────────────────────────

function computeBackoffMs(attempt: number): number {
  // 1s, 2s, 4s, 8s, 16s, then cap at 30s. attempt is 0-indexed.
  const raw = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_CAP_MS);
  const jitter = raw * JITTER_RATIO * Math.random();
  return Math.round(raw - raw * JITTER_RATIO + jitter);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * The thread the operator has open, shared by the module rather than held per
 * instance.
 *
 * The stream is mounted once, by the dashboard layout, so the instance that
 * owns the connection is not the screen that knows which thread is open. The
 * inbox publishes it here instead.
 */
const activeThreadRef: { current: string | null | undefined } = { current: null };

/**
 * Tell the shared stream which thread is open, without opening one.
 *
 * Only the thread-detail caches depend on this; everything else the stream
 * invalidates is list-level. A screen that unmounts hands the value back.
 */
export function useActiveInboxThread(threadId: string | null | undefined): void {
  useEffect(() => {
    activeThreadRef.current = threadId;
    return () => {
      activeThreadRef.current = null;
    };
  }, [threadId]);
}

export function useInboxStream(options: UseInboxStreamOptions = {}): UseInboxStreamResult {
  const { activeThreadId, enabled = true } = options;
  const qc = useQueryClient();

  const [status, setStatus] = useState<InboxStreamStatus>('idle');
  const [lastEventAt, setLastEventAt] = useState<Date | null>(null);
  const hasConnectedRef = useRef(false);

  // Everything the stream does with an event, so a tab that is only listening
  // on the broadcast channel reacts exactly as the tab holding the connection.
  const applyEvent = useCallback(
    (evt: InboxStreamEvent) => {
      setLastEventAt(new Date());
      applyGeneratingState(evt);
      const eventThreadId = (evt.data as Partial<InboxEventBase> | null)?.threadId;
      if (evt.type === 'thread.escalated' && eventThreadId !== activeThreadRef.current) {
        const escalation = evt.data as Partial<ThreadEscalatedPayload> | null;
        toast.info(`Iris escalated a conversation: ${escalationLabel(escalation?.trigger)}`, {
          description:
            escalation?.detail ??
            'It is waiting in the Escalated section of the inbox for a person to pick up.',
        });
      }
      if ((evt.type === 'message.received' || evt.type === 'message.sent') && evt.data) {
        applyRealtimeMessage(qc, evt.data as RealtimeMessagePayload, activeThreadRef.current);
      }
      invalidateForEvent(qc, evt, activeThreadRef);
    },
    [qc],
  );

  // One tab in the browser holds this connection; the rest are fed from it.
  // See `useStreamLease` for why three streams a tab does not fit.
  const { leader: live, publishEvent } = useLeasedStream<InboxStreamEvent>({
    name: 'inbox',
    want: enabled,
    status,
    applyEvent,
    applyStatus: (next) => setStatus(next as InboxStreamStatus),
  });
  // Mirror status into the inbox store so the messaging react-query
  // hooks can disable their poll fallbacks whenever SSE is `open` —
  // server pushes drive every invalidation, so polling at the same
  // time is wasted work (the user reported "polling messages, events
  // and threads constantly" on staging while SSE was working). The
  // store-level write only fires when the value actually changes
  // (setSseStatus is a no-op for repeat values), so the queries don't
  // re-render unnecessarily.
  const { setSseStatus } = useInboxActions();
  useEffect(() => {
    setSseStatus(status);
  }, [status, setSseStatus]);

  // Kept for the callers that still pass it. `undefined` means "not mine to
  // set" — the layout mounts the stream without knowing or caring which thread
  // is open, and must not overwrite what the inbox published.
  useEffect(() => {
    if (activeThreadId === undefined) return;
    activeThreadRef.current = activeThreadId;
  }, [activeThreadId]);

  useEffect(() => {
    if (!live) {
      setStatus('idle');
      return;
    }

    if (!supportsStreamingFetch()) {
      // Polling alone will keep the inbox alive — no error toast, no throw.
      setStatus('error');
      return;
    }

    const abortController = new AbortController();
    let cancelled = false;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const baseUrl = resolveOperationsBaseUrl();
    const fullUrl = `${baseUrl}${EVENTS_URL}`;

    const safeSetStatus = (next: InboxStreamStatus) => {
      if (!cancelled) setStatus(next);
    };

    async function connect(): Promise<void> {
      if (cancelled) return;
      safeSetStatus('connecting');

      // Pull the token at connect time so reconnects after a token refresh
      // (lib/api/session.ts) pick up the new value.
      const token = useAuthStore.getState().token;
      const tenantId = getActiveTenantId();

      if (!token) {
        // Without a token we can't authenticate — wait for an auth event by
        // closing quietly. The next mount (after login) will retry.
        safeSetStatus('closed');
        return;
      }

      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
      };
      if (tenantId) headers['x-tenant-id'] = tenantId;

      try {
        const response = await fetch(fullUrl, {
          method: 'GET',
          headers,
          signal: abortController.signal,
          credentials: 'omit',
          // `cache: 'no-store'` ensures no intermediate cache buffers SSE.
          cache: 'no-store',
        });

        if (response.status === 401) {
          // Reconnecting with the same expired JWT can never succeed. Retry
          // only once a newer token exists (renewed here or in another tab),
          // through the backoff so a token the server still refuses cannot
          // spin; otherwise follow the Axios client's logout path.
          if (await recoverSession(token)) {
            scheduleReconnect();
            return;
          }
          safeSetStatus('closed');
          redirectToLogin();
          return;
        }

        if (!response.ok || !response.body) {
          throw new Error(`SSE connect failed: ${response.status}`);
        }

        // Successful connect — reset the backoff counter.
        attempt = 0;
        // A reconnect means events were missed while the tab was away.
        if (hasConnectedRef.current) {
          void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
        }
        hasConnectedRef.current = true;
        safeSetStatus('open');

        await consumeStream(
          response,
          (evt) => {
            if (cancelled) return;
            applyEvent(evt);
            publishEvent(evt);
          },
          abortController.signal,
        );

        // Stream ended cleanly (server closed). Treat as a disconnect and
        // reconnect via backoff so a server roll doesn't kill realtime.
        if (!cancelled) {
          safeSetStatus('closed');
          scheduleReconnect();
        }
      } catch (err) {
        if (cancelled || abortController.signal.aborted) return;
        // Network blips, 5xx responses and proxy timeouts reconnect with
        // backoff. A 401 is handled above as a terminal session expiry.
        // eslint-disable-next-line no-console
        console.warn('[useInboxStream] connection error', err);
        safeSetStatus('error');
        scheduleReconnect();
      }
    }

    function scheduleReconnect(): void {
      if (cancelled) return;
      const delay = computeBackoffMs(attempt);
      attempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, delay);
    }

    void connect();

    return () => {
      cancelled = true;
      abortController.abort();
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };
    // `qc` from useQueryClient is stable for the lifetime of the provider, and
    // `activeThreadId` is read via ref. So the only meaningful dep is `enabled`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, qc, applyEvent, publishEvent]);

  return { status, lastEventAt };
}

// ─── Type exports for tests / consumers ──────────────────────────────────────

export type { MessageEventPayload, InboxStreamEvent };
